<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling-ownership.UI.2 / LINE.3 — "Pass the line for <inbox>":
// pick a teammate on the inbox (with their status) and until when — I take
// it back / end of today / in 2 hours. New calls ring them instead of me; a
// call I'm on stays with me. Opened from the status menu over the bus.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useKaptionCallStore } from './kaptionCallStore';
import { OPEN_PASS_LINE_EVENT } from './kaptionCallEvents';
import { resolveLineUntil as resolveUntil } from './kaptionCallModel';

const props = defineProps({
  currentUserId: { type: Number, default: null },
  now: { type: Function, default: () => new Date() },
});

const { t } = useI18n();
const calls = useKaptionCallStore();

const dialog = ref(null);
const inbox = ref(null);
const members = ref([]);
const selectedId = ref(null);
const until = ref('until_taken_back');
const isSaving = ref(false);

const UNTIL_OPTIONS = [
  { value: 'until_taken_back', label: 'KAPTION_CALLS.LINE.UNTIL_TAKEN_BACK' },
  { value: 'end_of_day', label: 'KAPTION_CALLS.LINE.UNTIL_END_OF_DAY' },
  { value: 'two_hours', label: 'KAPTION_CALLS.LINE.UNTIL_TWO_HOURS' },
];

const teammates = computed(() =>
  members.value.filter(member => member.id !== props.currentUserId)
);
const selected = computed(
  () => teammates.value.find(m => m.id === selectedId.value) || null
);
const isOnline = member => member.availability_status === 'online';
const statusKey = member => {
  if (isOnline(member)) return 'KAPTION_CALLS.LINE.STATUS_AVAILABLE';
  if (member.availability_status === 'busy')
    return 'KAPTION_CALLS.LINE.STATUS_BUSY';
  return 'KAPTION_CALLS.LINE.STATUS_OFFLINE';
};

const open = async ({ inboxId, inboxName } = {}) => {
  if (!inboxId) return;
  inbox.value = { id: inboxId, name: inboxName };
  until.value = 'until_taken_back';
  selectedId.value = null;
  dialog.value?.open();
  try {
    const response = await calls.api().getInboxMembers(inboxId);
    members.value = response?.data?.payload || [];
  } catch (e) {
    members.value = [];
  }
  selectedId.value = teammates.value.find(isOnline)?.id ?? null;
};

const confirm = async () => {
  if (!selected.value || !inbox.value) return;
  isSaving.value = true;
  const ok = await calls.passLine(inbox.value.id, {
    userId: selected.value.id,
    until: resolveUntil(until.value, props.now()),
  });
  isSaving.value = false;
  if (ok) dialog.value?.close();
};

onMounted(() => emitter.on(OPEN_PASS_LINE_EVENT, open));
onBeforeUnmount(() => emitter.off(OPEN_PASS_LINE_EVENT, open));

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    type="edit"
    width="md"
    :title="t('KAPTION_CALLS.LINE.DIALOG_TITLE', { inbox: inbox?.name || '' })"
    :description="t('KAPTION_CALLS.LINE.DIALOG_BODY')"
    :confirm-button-label="
      selected
        ? t('KAPTION_CALLS.LINE.CONFIRM', {
            name: (selected.name || '').split(' ')[0],
          })
        : t('KAPTION_CALLS.LINE.CONFIRM_EMPTY')
    "
    :cancel-button-label="t('KAPTION_CALLS.LINE.CANCEL')"
    :disable-confirm-button="!selected"
    :is-loading="isSaving"
    @confirm="confirm"
  >
    <div data-testid="kaption-pass-line-dialog" class="flex flex-col gap-3">
      <div
        role="radiogroup"
        class="flex max-h-60 flex-col gap-1 overflow-y-auto"
      >
        <p v-if="!teammates.length" class="px-2 py-1.5 text-sm text-n-slate-11">
          {{ t('KAPTION_CALLS.LINE.NO_TEAMMATES') }}
        </p>
        <button
          v-for="member in teammates"
          :key="member.id"
          type="button"
          role="radio"
          :data-testid="`kaption-pass-line-member-${member.id}`"
          :aria-checked="selectedId === member.id"
          class="flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left"
          :class="
            selectedId === member.id
              ? 'border-n-teal-8 bg-n-teal-3'
              : 'border-n-weak hover:bg-n-alpha-2'
          "
          @click="selectedId = member.id"
        >
          <Avatar
            :name="member.name"
            :src="member.thumbnail || member.avatar_url"
            :size="24"
            rounded-full
          />
          <span class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm text-n-slate-12">{{
              member.name
            }}</span>
            <span
              class="text-xs"
              :class="isOnline(member) ? 'text-n-teal-11' : 'text-n-slate-11'"
            >
              {{ t(statusKey(member)) }}
            </span>
          </span>
        </button>
      </div>
      <label class="flex items-center justify-between gap-3 text-sm">
        <span class="text-n-slate-11">{{ t('KAPTION_CALLS.LINE.UNTIL') }}</span>
        <select
          v-model="until"
          data-testid="kaption-pass-line-until"
          class="!mb-0 h-8 w-48 rounded-lg border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
        >
          <option
            v-for="option in UNTIL_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ t(option.label) }}
          </option>
        </select>
      </label>
    </div>
  </Dialog>
</template>
